import {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Combobox } from '@headlessui/react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconPlus, IconZoomOutline } from '@iwoca/icons';
import { fetchGetCompanySearch } from '@iwoca/lapi-client/edge';
import { Button, Input, Label, Spinner } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';

import styles from './CompanyInputSearchDE.module.css';
import { fetchGetLegalForm } from '../../../../../api/lendingApi/apiQueries';
import { useGetSelectStateQuery } from '../../../../../api/lendingApi/lendingApiQueries';
import {
  TGetCompanySearchResponseDE,
  TGetStateResponseDE,
} from '../../../../../lapiOverrideTypesDE';
import logger from '../../../../../utils/logger';
import ButtonLink from '../../../../ButtonLink/ButtonLink';
import { normaliseStepDataDE } from '../../../../helpers/stateNormalisation';
import { BUSINESS_STEP_ID } from '../../../../helpers/steps';
import { TCompanyDetailsDataDE } from '../../CompanyDetailsDE';

type TCompanySearchCompany = TGetCompanySearchResponseDE['data'][number];

type TProps = {
  setManualInputTrue: () => void;
  searchedCompaniesCount: number;
  setSearchedCompaniesCount: Dispatch<SetStateAction<number>>;
};

const CompanyInputSearchDE = ({
  setManualInputTrue,
  searchedCompaniesCount,
  setSearchedCompaniesCount,
}: TProps) => {
  const { normalisedState } = useGetSelectStateQuery(
    (data: TGetStateResponseDE) => normaliseStepDataDE(data)[BUSINESS_STEP_ID],
  );

  const { t } = useLingui();

  const {
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<TCompanyDetailsDataDE>();

  // Each time the API is called, we add one to this value. This lets us continue to show
  // the spinner until the last call is resolved
  const [numberOfCallsLoading, setNumberOfCallsLoading] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>();

  const [showDefaultCompany, setShowDefaultCompany] = useState(
    Boolean(getValues('companyName') && getValues('street')),
  );

  const [selectedCompany, setSelectedCompany] =
    useState<TCompanySearchCompany>();

  const [companyOptions, setCompanyOptions] = useState<TCompanySearchCompany[]>(
    [],
  );

  useEffect(() => {
    if (
      // normalised state will always exist because this component is not
      // rendered unless state exists, but left for defensive reasons.
      normalisedState &&
      normalisedState.companyName &&
      !normalisedState.crefoId
    ) {
      setShowDefaultCompany(false);
      setValue('companyName', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalisedState]);

  useEffect(() => {
    // Search "my company" for a fake STAGING company
    const companySearch = setTimeout(async () => {
      setNumberOfCallsLoading((prevNumber) => prevNumber + 1);

      if (searchQuery) {
        setSearchedCompaniesCount((prevCount) => prevCount + 1);

        tracking.ampli.companySearchUsed({
          query_length: searchQuery.length,
        });
      }

      //limit fetch to company_search until search query has 2 or more characters
      if (searchQuery && searchQuery.length >= 2) {
        try {
          // @ts-expect-error LAPI client uses UK typing, overriding to DE
          const response = (await fetchGetCompanySearch({
            query: { company_name: searchQuery },
          })) as TGetCompanySearchResponseDE;

          tracking.ampli.companySearchResponseShown({
            query_length: searchQuery.length,
            response_length: response.data.length,
          });

          setCompanyOptions(response.data);
        } catch {
          setCompanyOptions([]);
        }
      } else {
        setCompanyOptions([]);
        setSelectedCompany(undefined);
      }
      setNumberOfCallsLoading((prevNumber) => prevNumber - 1);
    }, 250);

    return () => {
      clearTimeout(companySearch);
    };
  }, [searchQuery, setSearchedCompaniesCount]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && numberOfCallsLoading > 0) {
      e.preventDefault();
    }
  };

  const handleSetCompanyType = async (crefoId: string) => {
    try {
      const response = await fetchGetLegalForm(crefoId);

      if (!response?.data?.legal_form) {
        throw new Error('Invalid response for legal form');
      }

      setValue('legalForm', response?.data?.legal_form, {
        shouldValidate: true,
      });
    } catch (error) {
      if (error instanceof Error) {
        logger.error('Failed to set company type', {
          error: error.message,
        });
      }
    }
  };

  const handleChange = async (company: TCompanySearchCompany) => {
    tracking.ampli.companyNameSearched();
    setSelectedCompany(company);

    setValue('companyName', company.name, {
      shouldValidate: true,
    });

    setValue('crefoId', company.crefo_id, {
      shouldValidate: true,
    });

    setValue('buildingNumber', company.house_number, {
      shouldValidate: true,
    });

    setValue('street', company.street, {
      shouldValidate: true,
    });

    setValue('postcode', company.postcode, {
      shouldValidate: true,
    });

    setValue('town', company.city, {
      shouldValidate: true,
    });

    await handleSetCompanyType(company.crefo_id);
  };

  const resetSelectedCompany = () => {
    setSearchQuery(undefined);
    setSelectedCompany(undefined);
    setShowDefaultCompany(false);

    setValue('companyName', '');
    setValue('crefoId', '');
    setValue('buildingNumber', '');
    setValue('street', '');
    setValue('postcode', '');
    setValue('town', '');
    setValue('legalForm', undefined);
  };

  const buildDefaultValue = () =>
    getValues('companyName') && getValues('town')
      ? `${getValues('companyName')} | ${getValues('town')}`
      : '';

  const handleEnterManuallyClicked = (
    position: 'Complete' | 'Incomplete' | 'Not found',
  ) => {
    tracking.ampli.enterCompanyManuallyClicked({
      searched_companies_count: searchedCompaniesCount,
      version_of_button: position,
    });

    setManualInputTrue();
  };

  const renderOptions = useCallback(() => {
    if (numberOfCallsLoading > 0) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      );
    }

    return (
      <>
        {companyOptions.length > 0 ? (
          companyOptions.map((company, index) => (
            <Combobox.Option
              className={({ active }) =>
                clsx(
                  styles.option,
                  {
                    [styles.active]: active,
                  },
                  'fs-mask',
                  'dd-privacy-mask',
                )
              }
              key={index}
              value={company}
            >
              {company.name} | {company.city}
            </Combobox.Option>
          ))
        ) : (
          <>
            <p className={styles.noResults}>
              <Trans>No results</Trans>
            </p>
            <p className={styles.enterManuallyText}>
              <span>
                <Trans>
                  Have you entered your company name correctly? If it still
                  doesn’t work, then
                </Trans>

                <ButtonLink
                  className={styles.enterManually}
                  type="button"
                  onClick={() => handleEnterManuallyClicked('Not found')}
                >
                  <Trans>Click here to enter the data manually.</Trans>
                </ButtonLink>
              </span>
            </p>
          </>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfCallsLoading]);

  return (
    <div className={styles.companySearchContainer}>
      <Combobox onChange={handleChange}>
        <div className={styles.companiesSearch}>
          {selectedCompany || showDefaultCompany ? (
            <>
              <div className={styles.companyNameLabel}>
                <Label htmlFor="companyDisplayName">
                  <Trans>Limited company</Trans>
                </Label>

                <ButtonLink
                  className={styles.enterManually}
                  type="button"
                  onClick={() => handleEnterManuallyClicked('Complete')}
                >
                  <Trans>Company not found?</Trans>
                </ButtonLink>
              </div>

              <Input
                id="companyDisplayName"
                className={`${styles.companyInput} fs-mask`}
                value={buildDefaultValue()}
                disabled
                error={
                  (errors?.companyName?.message &&
                    errors.companyName.message) ||
                  (errors?.street?.message && errors.street.message)
                }
              />
              <Button
                className={styles.clearButton}
                type="reset"
                variant="tertiary"
                size="small"
                data-testid="clear-button-company"
                onClick={resetSelectedCompany}
              >
                <IconPlus
                  className={styles.clearButtonIcon}
                  height="24"
                  width="24"
                />
              </Button>
            </>
          ) : (
            <>
              <div className={styles.companyNameLabel}>
                <Combobox.Label htmlFor="companyName">
                  <Trans>Limited company</Trans>
                </Combobox.Label>

                <ButtonLink
                  className={styles.enterManually}
                  type="button"
                  onClick={() => handleEnterManuallyClicked('Incomplete')}
                >
                  <Trans>Company not found?</Trans>
                </ButtonLink>
              </div>

              <Combobox.Input
                as={Input}
                iconRight={!selectedCompany ? <IconZoomOutline /> : undefined}
                placeholder={t`Search a company name or number`}
                className={`${styles.companyInput} fs-mask`}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={handleKeyDown}
                error={
                  (errors?.companyName?.message &&
                    errors.companyName.message) ||
                  (errors?.street?.message && errors.street.message)
                }
              />
            </>
          )}
          {searchQuery && (
            <Combobox.Options className={styles.options}>
              {renderOptions()}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
    </div>
  );
};

export default CompanyInputSearchDE;
